// Export the selection (or the whole board) as PNG or SVG (spec §12, phase 6).
import type { BoardObject } from '../../shared/types.ts';
import type { Editor } from '../editor.ts';
import { variantFor } from '../engine/images.ts';
import { withFrameChildren } from '../model/frames.ts';
import { download } from '../util/download.ts';
import { drawScene, paintOrder, sceneBounds } from './scene.ts';
import { buildSvg } from './svg.ts';

const PAD = 32;
const PNG_SCALE = 2;
const MAX_SIDE = 8192;
const MAX_PIXELS = 40_000_000;

/**
 * What to export: the selection plus frame contents and any connectors that
 * run between exported objects; or, with nothing selected, everything.
 */
export function exportObjects(ed: Editor): BoardObject[] {
  if (!ed.selection.size) return [...ed.store.orderedIds()].map((id) => ed.store.get(id)!);
  const chosen = withFrameChildren(ed.store, ed.selectedObjects());
  const ids = new Set(chosen.map((o) => o.id));
  for (const cid of ed.connectors.attachedTo(ids)) {
    const c = ed.store.get(cid);
    if (c?.type !== 'connector' || ids.has(cid)) continue;
    const ends = [c.from, c.to].map((e) => ('objectId' in e ? ids.has(e.objectId) : false));
    if (ends.every(Boolean)) chosen.push(c);
  }
  return paintOrder(chosen);
}

export async function exportPng(ed: Editor, name: string): Promise<void> {
  const objs = exportObjects(ed);
  const get = (id: string) => ed.store.get(id);
  const bounds = sceneBounds(objs, get, PAD);
  if (!bounds) throw new Error('There is nothing to export.');
  const scale = Math.min(PNG_SCALE, MAX_SIDE / bounds.w, MAX_SIDE / bounds.h, Math.sqrt(MAX_PIXELS / (bounds.w * bounds.h)));

  // Exports can't show placeholders: wait for each image at the resolution it'll be drawn.
  await Promise.all(objs.flatMap((o) => (o.type === 'image' ? [ed.images.ensure(o.assetHash, variantFor(Math.max(o.w, o.h) * scale))] : [])));

  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(bounds.w * scale);
  canvas.height = Math.ceil(bounds.h * scale);
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#FFFFFF';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.imageSmoothingQuality = 'high';
  drawScene(ctx, objs, { get, layouts: ed.layouts, images: ed.images }, { x: bounds.x, y: bounds.y, scale });
  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) throw new Error('The browser could not create the image.');
  download(blob, `${name}.png`);
}

export async function exportSvg(ed: Editor, name: string): Promise<void> {
  const objs = exportObjects(ed);
  const get = (id: string) => ed.store.get(id);
  const bounds = sceneBounds(objs, get, PAD);
  if (!bounds) throw new Error('There is nothing to export.');

  // Embed images so the file stands alone (asset URLs need a signed-in session).
  const hrefs = new Map<string, string>();
  await Promise.all([...new Set(objs.flatMap((o) => (o.type === 'image' ? [o.assetHash] : [])))].map(async (hash) => {
    const res = await fetch(`/assets/${hash}/original`, { credentials: 'same-origin' });
    if (res.ok) hrefs.set(hash, await blobToDataUrl(await res.blob()));
  }));

  const svg = buildSvg(objs, { get, layoutOf: (o) => ed.layouts.get(o), imageHref: (h) => hrefs.get(h) ?? null }, bounds);
  download(new Blob([svg], { type: 'image/svg+xml' }), `${name}.svg`);
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(blob);
  });
}
